import uidAtom from '~/recoil/atom/uid';
import taxInfoAtom from '~/recoil/atom/taxInfo';
import taxTypeAtom from '~/recoil/atom/taxType';
import cartTypeAtom from '~/recoil/atom/cartType';
import cartStatusAtom from '~/recoil/atom/cartStatus';
import pickMealAtAtom from '~/recoil/atom/pickMealAt';
import pickMealEndAtom from '~/recoil/atom/pickMealEnd';
import couponAtom from '~/recoil/atom/coupon';
import campaignIdAtom from '~/recoil/atom/campaignId';
import { useSetRecoilState } from 'recoil';
import { CartStatus, CartType, PlatformCartInFirestore, TaxType } from '~/types/cart';
import enterpriseGroupBuyOrdersAtom from '~/recoil/atom/enterpriseGroupBuy/enterpriseGroupBuyOrders';
import { PLATFORM_ID } from '~/globalVariables';
import { firestore } from '~/firebase/init';
import { doc, GeoPoint, getDoc, setDoc } from 'firebase/firestore';
import {
  EnterpriseGroupBuy,
  EnterpriseGroupBuyPaymentType,
  EnterpriseGroupBuyStatus,
} from '~/types/enterpriseGroupBuy';
import orderType from '~/recoil/atom/orderType';
import { OrderType, PaymentType } from '~/types/common';
import paymentTypeAtom from '~/recoil/atom/paymentType';

export default function useEnterpriseGroupBuyCart() {
  const setUid = useSetRecoilState(uidAtom);
  const setOrders = useSetRecoilState(enterpriseGroupBuyOrdersAtom);
  const setTaxInfo = useSetRecoilState(taxInfoAtom);
  const setTaxType = useSetRecoilState(taxTypeAtom);
  const setCartType = useSetRecoilState(cartTypeAtom);
  const setCartStatus = useSetRecoilState(cartStatusAtom);
  const setPickMealAt = useSetRecoilState(pickMealAtAtom);
  const setPickMealEnd = useSetRecoilState(pickMealEndAtom);
  const setPaymentType = useSetRecoilState(paymentTypeAtom);
  const setCoupon = useSetRecoilState(couponAtom);
  const setCampaignId = useSetRecoilState(campaignIdAtom);
  const setOrderType = useSetRecoilState(orderType);

  const handleInitEnterpriseGroupBuyCart = async (
    enterpriseGroupBuy: EnterpriseGroupBuy,
    userId: string,
  ) => {
    const cartId = `enterprise_group_buy_${enterpriseGroupBuy.id}_${userId}`;
    const cartRef = doc(firestore, 'platforms', PLATFORM_ID, 'carts', cartId);

    console.log(enterpriseGroupBuy);

    setCoupon(null);
    setCampaignId('');
    setUid(userId);
    setPickMealAt(enterpriseGroupBuy.pickMealAt.toDate());
    setPickMealEnd(enterpriseGroupBuy.pickMealEnd.toDate());
    setOrderType(OrderType.PLATFORM_PICKUP_POINT);
    setCartType(CartType.CART_PERSONAL);

    const cartDefaultPaymentType =
      enterpriseGroupBuy.paymentType !== EnterpriseGroupBuyPaymentType.CONSUMER
        ? PaymentType.PLATFORM
        : PaymentType.LINEPAY;

    const defaultTaxType =
      enterpriseGroupBuy.paymentType !== EnterpriseGroupBuyPaymentType.CONSUMER
        ? TaxType.COMPANY
        : TaxType.PERSONAL;

    try {
      const cartSnap = await getDoc(cartRef);
      const cartData = cartSnap.data() as PlatformCartInFirestore;

      if (
        cartSnap.exists() &&
        cartData?.status === CartStatus.ACTIVE &&
        enterpriseGroupBuy.status === EnterpriseGroupBuyStatus.ONGOING
      ) {
        setOrders(cartData.orders);
        setPaymentType(cartData.paymentType);
        setTaxType(cartData.taxType);
        setTaxInfo(cartData.taxInfo);
        setCartStatus(cartData?.status);
      } else {
        const status =
          !!cartData &&
          cartData?.status !== CartStatus.ACTIVE &&
          enterpriseGroupBuy.status !== EnterpriseGroupBuyStatus.ONGOING
            ? CartStatus.CANCELED
            : CartStatus.ACTIVE;

        const defaultData = {
          enterpriseGroupBuyId: enterpriseGroupBuy.id,
          uid: userId,
          orders: [],
          paymentType: cartDefaultPaymentType,
          pickMealAt: enterpriseGroupBuy.pickMealAt,
          pickMealEnd: enterpriseGroupBuy.pickMealEnd,
          taxInfo:
            !!enterpriseGroupBuy?.invoice?.invoiceTaxId &&
            !!enterpriseGroupBuy?.invoice?.invoiceTitle
              ? {
                  invoice: enterpriseGroupBuy?.invoice?.invoiceTaxId || '',
                  title: enterpriseGroupBuy?.invoice?.invoiceTitle || '',
                }
              : { invoice: '', title: '' },
          taxType: defaultTaxType,
          orderType: OrderType.PLATFORM_PICKUP_POINT,
          cartType: CartType.CART_PERSONAL,
          status,
          pickMealPoint: {
            id: enterpriseGroupBuy?.miniCode,
            address: enterpriseGroupBuy?.address,
            name: enterpriseGroupBuy.name,
            geoPoint: new GeoPoint(0, 0),
          },
        };
        setOrders([]);
        setCartStatus(status);
        setPaymentType(cartDefaultPaymentType);
        setTaxType(defaultTaxType);
        setTaxInfo({
          invoice: enterpriseGroupBuy?.invoice?.invoiceTaxId || '',
          title: enterpriseGroupBuy?.invoice?.invoiceTitle || '',
        });

        await setDoc(cartRef, defaultData);
      }
    } catch (error) {
      console.error('Error handling cart:', error);
    }
  };

  return { handleInitEnterpriseGroupBuyCart };
}
